import { MessageSquare, Send } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface ChatInterfaceProps {
  onSendMessage: (message: string) => void;
}

export const ChatInterface = ({
  onSendMessage,
}: ChatInterfaceProps): JSX.Element => {
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async () => {
    if (newMessage.trim().length === 0) {
      return;
    }

    setIsLoading(true);

    // Send the message to the parent component
    onSendMessage(newMessage);

    setNewMessage('');
    setIsLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 h-fit">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          <FormattedMessage id="rca.chat.title" />
        </h3>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        <FormattedMessage id="rca.chat.description" />
      </p>

      {/* Input */}
      <div className="space-y-3">
        <Textarea
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Annie about this investigation..."
          className="w-full min-h-[80px] resize-none"
          disabled={isLoading}
        />
        <Button
          onClick={handleSendMessage}
          disabled={newMessage.trim().length === 0 || isLoading}
          className="w-full"
        >
          <Send className="w-4 h-4 mr-2" />
          <FormattedMessage id="rca.chat.send" />
        </Button>
      </div>
    </div>
  );
};
